"use client";

import {
  sdkActionManager,
  useEmbedNonStylesConfig,
  useEmbedStyles,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import { useRouterQuery } from "@calcom/lib/hooks/useRouterQuery";
import useTheme from "@calcom/lib/hooks/useTheme";
import { UserAvatar } from "@calcom/ui/components/avatar";
import { Icon } from "@calcom/ui/components/icon";
import { OrgBanner } from "@calcom/ui/components/organization-banner";
import { UnpublishedEntity } from "@calcom/ui/components/unpublished-entity";
import { EventTypeDescriptionLazy as EventTypeDescription } from "@calcom/web/modules/event-types/components";
import EmptyPage from "@calcom/web/modules/event-types/components/EmptyPage";
import PoweredBy from "@calcom/web/modules/ee/common/components/PoweredBy";
import type { getServerSideProps } from "@server/lib/[user]/getServerSideProps";
import classNames from "classnames";
import type { InferGetServerSidePropsType } from "next";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import AvailabilityExplorer from "@components/team/screens/AvailabilityExplorer";
import { Toaster } from "sonner";

type StaffMember = {
  id: number;
  name: string;
  avatarUrl: string | null;
  calUsername: string | null;
  locationId: number | null;
};

function PartnerBrandingEffect({ branding }: { branding?: { name?: string | null; logo?: string | null; favicon?: string | null; primaryColor?: string | null; accentColor?: string | null } | null }) {
  useEffect(() => {
    if (!branding) return;
    if (branding.favicon) {
      const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']") || (() => {
        const el = document.createElement("link");
        el.rel = "icon";
        document.head.appendChild(el);
        return el;
      })();
      link.href = branding.favicon;
    }
    if (branding.primaryColor) {
      document.documentElement.style.setProperty("--brand-color", branding.primaryColor);
    }
    if (branding.accentColor) {
      document.documentElement.style.setProperty("--brand-accent", branding.accentColor);
    }
    if (branding.name) {
      document.title = branding.name;
    }
  }, [branding]);
  return null;
}

type ConnectLocation = { id: number; name: string; slug: string | null; address: string | null; city: string | null; state: string | null; zipCode: string | null; phone: string | null; timezone: string | null };

export type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
export function UserPage(props: PageProps) {
  const { users, profile, eventTypes, entity, userId } = props;
  const connectLocations: ConnectLocation[] = (props as any).connectLocations ?? [];
  const partnerBranding = (props as any).partnerBranding;

  const [user] = users;
  useTheme(profile.theme);

  const isMultiLocation = connectLocations.length >= 2;

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  // Staff picker state
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const [staffPickerType, setStaffPickerType] = useState<typeof eventTypes[0] | null>(null);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const staffPickerRef = useRef<HTMLDivElement>(null);

  function buildBookingUrl(calUsername: string, typeSlug: string, locId?: number | null) {
    const base = `/${calUsername}/${typeSlug}`;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (typeof v === "string") params.set(k, v);
      else if (Array.isArray(v) && v[0]) params.set(k, v[0]);
    }
    if (locId) params.set("locationId", String(locId));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  async function openStaffPicker(type: typeof eventTypes[0]) {
    sdkActionManager?.fire("eventTypeSelected", { eventType: type });
    const isServiceMode = !!(type.metadata as any)?.serviceMode;
    if (!isServiceMode) {
      window.location.href = buildBookingUrl(user.profile.username ?? "", type.slug, selectedLocationId);
      return;
    }
    setStaffPickerType(type);
    setStaffPickerOpen(true);
    setStaffList([]);
    setStaffLoading(true);
    try {
      const params = new URLSearchParams({
        ownerUsername: user.profile.username ?? "",
        eventTypeId: String(type.id),
        ...(selectedLocationId ? { locationId: String(selectedLocationId) } : {}),
      });
      const data = await fetch(`/api/services/staff-for-booking?${params}`).then((r) => r.ok ? r.json() : { staff: [] });
      setStaffList(data.staff ?? []);
    } catch {
      setStaffList([]);
    } finally {
      setStaffLoading(false);
    }
  }

  // Close staff picker on outside click
  useEffect(() => {
    if (!staffPickerOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (staffPickerRef.current && !staffPickerRef.current.contains(e.target as Node)) {
        setStaffPickerOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [staffPickerOpen]);

  useEffect(() => {
    if (!isMultiLocation) return;
    const params = new URLSearchParams(window.location.search);
    const v = params.get("location");
    const parsed = v ? parseInt(v, 10) : NaN;
    if (Number.isFinite(parsed)) setSelectedLocationId(parsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectLocation = (id: number) => {
    setSelectedLocationId((prev) => {
      const next = prev === id ? null : id;
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (next === null) {
          url.searchParams.delete("location");
        } else {
          url.searchParams.set("location", String(next));
        }
        window.history.replaceState(null, "", url.pathname + url.search);
      }
      return next;
    });
  };

  const isBioEmpty = !user.bio || !user.bio.replace("<p><br></p>", "").length;

  const isEmbed = useIsEmbed(props.isEmbed);
  const eventTypeListItemEmbedStyles = useEmbedStyles("eventTypeListItem");
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
  const {
    user: _user,
    orgSlug: _orgSlug,
    redirect: _redirect,
    location: _location,
    ...query
  } = useRouterQuery();

  if (entity.considerUnpublished) {
    return (
      <div className="flex h-full min-h-[calc(100dvh)] items-center justify-center">
        <UnpublishedEntity {...entity} />
      </div>
    );
  }

  // Filter event types by selected location.
  // Events with no location links are available at ANY location.
  const visibleEventTypes = isMultiLocation && selectedLocationId !== null
    ? eventTypes.filter((et) => {
        // Check the multi-location join table array first (preferred)
        const svcIds: number[] | undefined = (et as any).serviceLocationIds;
        if (Array.isArray(svcIds) && svcIds.length > 0) {
          return svcIds.includes(selectedLocationId);
        }
        // Fall back to single connectLocationId
        const raw = (et as any).connectLocationId;
        if (raw == null) return true;
        return Number(raw) === selectedLocationId;
      })
    : eventTypes;

  // Shape for AvailabilityExplorer
  const explorerEventTypes = eventTypes.map((et) => ({
    id: et.id,
    title: et.title,
    slug: et.slug,
    length: et.length,
    connectLocationId: (et as any).connectLocationId ?? null,
    teamSlug: null as string | null,
  }));

  const isEventListEmpty = visibleEventTypes.length === 0;
  const isOrg = !!user?.profile?.organization;

  const selectedLocation = connectLocations.find((l) => l.id === selectedLocationId) ?? null;

  return (
    <>
      <PartnerBrandingEffect branding={partnerBranding} />
      <div className={classNames(shouldAlignCentrally ? "mx-auto" : "", isEmbed ? "max-w-3xl" : "")}>
        {partnerBranding?.logo && (
          <div className="flex justify-center pt-6 pb-2">
            <img src={partnerBranding.logo} alt={partnerBranding.name || "Partner"} className="h-8 object-contain" />
          </div>
        )}
        <main
          className={classNames(
            shouldAlignCentrally ? "mx-auto" : "",
            isEmbed ? "border-booker border-booker-width  bg-default rounded-md" : "",
            "max-w-3xl px-4 py-12"
          )}>
          {/* Profile card */}
          <div className="border-subtle bg-default text-default mb-8 overflow-hidden rounded-xl border">
            {isOrg && user.profile.organization?.bannerUrl && (
              <OrgBanner
                alt={user.profile.organization.name ?? "Organization banner"}
                imageSrc={user.profile.organization.bannerUrl}
                className="p-1 border border-subtle rounded-xl w-full object-cover"
              />
            )}
            <div className="p-4">
              <UserAvatar
                size="lg"
                user={{
                  avatarUrl: user.avatarUrl,
                  profile: user.profile,
                  name: profile.name,
                  username: profile.username,
                }}
                className={isOrg && user.profile.organization?.bannerUrl ? "-mt-14" : ""}
              />
              <h1 className={classNames("font-cal text-emphasis mb-1 text-xl", isOrg && user.profile.organization?.bannerUrl ? "" : "mt-4")} data-testid="name-title">
                {profile.name}
                {!isOrg && user.verified && (
                  <Icon
                    name="badge-check"
                    className="mx-1 -mt-1 inline h-6 w-6 fill-blue-500 text-white dark:text-black"
                  />
                )}
                {isOrg && (
                  <Icon
                    name="badge-check"
                    className="mx-1 -mt-1 inline h-6 w-6 fill-yellow-500 text-white dark:text-black"
                  />
                )}
              </h1>
              {!isBioEmpty && (
                <>
                  {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized via safeBio */}
                  <div
                    className="text-default wrap-break-word text-sm [&_a]:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-600"
                    dangerouslySetInnerHTML={{ __html: props.safeBio }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Location filter chips — always visible when 2+ locations, events shown below regardless */}
          {isMultiLocation && (
            <div className="mb-5">
              <p className="text-subtle mb-2.5 text-xs font-medium uppercase tracking-wide">Filter by location</p>
              <div className="flex flex-wrap gap-2">
                {connectLocations.map((loc) => {
                  const isSelected = selectedLocationId === loc.id;
                  const parts = [loc.city, loc.state].filter(Boolean).join(", ");
                  return (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => handleSelectLocation(loc.id)}
                      className={classNames(
                        "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all",
                        isSelected
                          ? "border-emphasis bg-emphasis text-emphasis shadow-sm"
                          : "border-subtle bg-default text-default hover:border-emphasis hover:bg-subtle"
                      )}>
                      <Icon name="map-pin" className="h-3 w-3 shrink-0" />
                      <span>{loc.name}</span>
                      {parts && (
                        <span className={classNames("text-xs", isSelected ? "opacity-70" : "text-subtle")}>
                          · {parts}
                        </span>
                      )}
                      {isSelected && (
                        <Icon name="x" className="ml-0.5 h-3 w-3 shrink-0 opacity-60" />
                      )}
                    </button>
                  );
                })}
                {selectedLocationId !== null && (
                  <button
                    type="button"
                    onClick={() => handleSelectLocation(selectedLocationId)}
                    className="text-subtle hover:text-emphasis inline-flex items-center gap-1 text-sm transition-colors">
                    <Icon name="x" className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>
              {selectedLocation && (
                <div className="mt-2.5 flex items-center gap-1.5">
                  <Icon name="map-pin" className="text-subtle h-3.5 w-3.5 shrink-0" />
                  <span className="text-subtle text-xs">
                    {[selectedLocation.address, selectedLocation.city, selectedLocation.state, selectedLocation.zipCode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Event type list */}
          <div
            className={classNames("rounded-md", !isEventListEmpty && "border-subtle border")}
            data-testid="event-types">
            {visibleEventTypes.map((type) => {
              const isServiceType = !!(type.metadata as any)?.serviceMode;
              // For service-mode events: intercept click to show staff picker.
              // For normal events: use a regular Link.
              if (isServiceType) {
                return (
                  <button
                    key={type.id}
                    type="button"
                    style={{ display: "flex", ...eventTypeListItemEmbedStyles }}
                    onClick={() => openStaffPicker(type)}
                    className="bg-default border-subtle dark:bg-cal-muted dark:hover:bg-subtle hover:bg-cal-muted group relative w-full border-b text-left transition first:rounded-t-md last:rounded-b-md last:border-b-0"
                    data-testid="event-type-link">
                    <Icon
                      name="arrow-right"
                      className="text-emphasis absolute right-4 top-4 h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
                    />
                    <div className="block w-full p-5">
                      <div className="flex flex-wrap items-center">
                        <h2 className="text-default pr-2 text-sm font-semibold">{type.title}</h2>
                      </div>
                      <EventTypeDescription eventType={type} isPublic={true} shortenDescription />
                    </div>
                  </button>
                );
              }
              return (
                <Link
                  key={type.id}
                  style={{ display: "flex", ...eventTypeListItemEmbedStyles }}
                  prefetch={false}
                  href={buildBookingUrl(user.profile.username ?? "", type.slug, selectedLocationId)}
                  passHref
                  onClick={() => sdkActionManager?.fire("eventTypeSelected", { eventType: type })}
                  className="bg-default border-subtle dark:bg-cal-muted dark:hover:bg-subtle hover:bg-cal-muted group relative border-b transition first:rounded-t-md last:rounded-b-md last:border-b-0"
                  data-testid="event-type-link">
                  <Icon
                    name="arrow-right"
                    className="text-emphasis absolute right-4 top-4 h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
                  />
                  <div className="block w-full p-5">
                    <div className="flex flex-wrap items-center">
                      <h2 className="text-default pr-2 text-sm font-semibold">{type.title}</h2>
                    </div>
                    <EventTypeDescription eventType={type} isPublic={true} shortenDescription />
                  </div>
                </Link>
              );
            })}
          </div>

          {isEventListEmpty && (
            <EmptyPage name={profile.name || "User"} />
          )}

          {/* Compare availability across locations */}
          {isMultiLocation && explorerEventTypes.length > 0 && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="border-subtle w-full border-t" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-default text-subtle px-3 text-xs">or</span>
                </div>
              </div>
              <div className="mt-4">
                <AvailabilityExplorer
                  locations={connectLocations}
                  orgEventTypes={explorerEventTypes}
                  orgTeamId={0}
                />
              </div>
            </div>
          )}
        </main>
        {!props.isBrandingHidden && (
          <div className="mx-auto max-w-3xl pb-4">
            <PoweredBy />
          </div>
        )}
        <Toaster position="bottom-right" />
      </div>

      {/* Staff picker overlay — shown when a service-mode event type is clicked */}
      {staffPickerOpen && staffPickerType && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div
            ref={staffPickerRef}
            className="bg-default border-subtle w-full max-w-sm rounded-xl border shadow-xl">
            {/* Header */}
            <div className="border-subtle flex items-center justify-between border-b px-5 py-4">
              <div>
                <p className="text-emphasis text-sm font-semibold">{staffPickerType.title}</p>
                <p className="text-subtle mt-0.5 text-xs">Choose a team member</p>
              </div>
              <button
                type="button"
                onClick={() => setStaffPickerOpen(false)}
                className="text-subtle hover:text-emphasis rounded-md p-1 transition-colors">
                <Icon name="x" className="h-4 w-4" />
              </button>
            </div>

            {/* Staff list */}
            <div className="max-h-80 overflow-y-auto p-2">
              {staffLoading ? (
                <div className="text-subtle py-6 text-center text-sm">Loading team members…</div>
              ) : (
                <>
                  {/* No preference option */}
                  <a
                    href={buildBookingUrl(user.profile.username ?? "", staffPickerType.slug, selectedLocationId)}
                    className="hover:bg-subtle flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-colors">
                    <div className="border-subtle bg-default flex h-9 w-9 shrink-0 items-center justify-center rounded-full border">
                      <Icon name="users" className="text-subtle h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-emphasis text-sm font-medium">No preference</p>
                      <p className="text-subtle text-xs">First available team member</p>
                    </div>
                  </a>

                  {staffList.map((member) => {
                    const bookingUsername = member.calUsername ?? (user.profile.username ?? "");
                    const bookingUrl = buildBookingUrl(bookingUsername, staffPickerType.slug, selectedLocationId);
                    return (
                      <a
                        key={member.id}
                        href={bookingUrl}
                        className="hover:bg-subtle flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-colors">
                        {member.avatarUrl ? (
                          <img
                            src={member.avatarUrl}
                            alt={member.name}
                            className="h-9 w-9 shrink-0 rounded-full object-cover"
                          />
                        ) : (
                          <div className="border-subtle bg-subtle flex h-9 w-9 shrink-0 items-center justify-center rounded-full border">
                            <span className="text-emphasis text-xs font-semibold uppercase">
                              {member.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <p className="text-emphasis text-sm font-medium">{member.name}</p>
                      </a>
                    );
                  })}

                  {!staffLoading && staffList.length === 0 && (
                    <p className="text-subtle px-3 py-2 text-xs">No additional team members configured.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserPage;
